import React, {useEffect, useState} from 'react';
import {Badge, Button, Dropdown, List, message, Modal, Space, Tag, Tooltip, Typography} from 'antd';
import {DeleteOutlined, DownloadOutlined, ExclamationCircleOutlined} from '@ant-design/icons';
import {
  clearFailedFiles,
  FailedFileInfo,
  getFailedFiles,
  removeFailedFile,
  subscribeToUploadState
} from '../../helpers/uploadState';
import {formatFileSize} from '../../helpers/file';
import {downloadToCsv, downloadToExcel} from '../../helpers/csv';

const {Text} = Typography;

const FailedFiles: React.FC = () => {
  const [failedFiles, setFailedFiles] = useState<FailedFileInfo[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);

  // 更新失败文件列表
  const updateFailedFiles = () => {
    setFailedFiles(getFailedFiles());
  };

  useEffect(() => {
    // 初始加载
    updateFailedFiles();
    
    // 订阅状态变化
    const unsubscribe = subscribeToUploadState(updateFailedFiles);
    
    return unsubscribe;
  }, []);

  const handleClearAll = () => {
    clearFailedFiles();
    setIsModalVisible(false);
  };

  const handleRemoveFile = (fileName: string) => {
    removeFailedFile(fileName);
  };


  // 导出失败文件列表
  const handleExport = (type: 'csv' | 'excel') => {
    if (failedFiles.length === 0) {
      message.warning('没有失败文件可以导出');
      return;
    }

    // 准备导出数据
    const exportData = failedFiles.map(file => ({
      fileName: file.fileName,
      errorMessage: file.errorMessage,
      fileSize: file.fileSize ? formatFileSize(file.fileSize) : '未知',
      fileType: file.fileType || '未知',
      timestamp: new Date(file.timestamp).toLocaleString('zh-CN'),
      errorType: getErrorType(file.errorMessage).text
    }));

    // 字段映射
    const keyMap = {
      fileName: '文件名',
      errorMessage: '错误信息',
      fileSize: '文件大小',
      fileType: '文件类型',
      timestamp: '失败时间',
      errorType: '错误类型'
    };

    const fileName = `上传失败文件列表_${new Date().toISOString().slice(0, 10)}`;

    try {
      if (type === 'csv') {
        downloadToCsv(exportData, keyMap, fileName);
        message.success('CSV文件导出成功');
      } else {
        downloadToExcel(exportData, keyMap, fileName);
        message.success('Excel文件导出成功');
      }
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败，请重试');
    }
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('zh-CN');
  };

  const getErrorType = (errorMessage: string) => {
    if (errorMessage.includes('文件太大了')) {
      return {color: 'red', text: '文件过大'};
    } else if (errorMessage.includes('网络')) {
      return {color: 'orange', text: '网络错误'};
    } else if (errorMessage.includes('格式')) {
      return {color: 'blue', text: '格式错误'};
    } else {
      return {color: 'default', text: '其他错误'};
    }
  };

  if (failedFiles.length === 0) {
    return null;
  }

  return (
    <>
      <Tooltip title={`有 ${failedFiles.length} 个文件上传失败，点击查看详情`}>
        <Button
          type="text"
          icon={<ExclamationCircleOutlined />}
          onClick={() => setIsModalVisible(true)}
          style={{ color: '#ff4d4f' }}
        >
          <Badge count={failedFiles.length} size="small">
            失败文件
          </Badge>
        </Button>
      </Tooltip>

      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            <span>上传失败文件列表</span>
            <Badge count={failedFiles.length} />
          </Space>
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        width={800}
        footer={[
          <Dropdown
            key="export"
            menu={{
              items: [
                {
                  key: 'csv',
                  label: '导出为CSV',
                  icon: <DownloadOutlined />,
                  onClick: () => handleExport('csv')
                },
                {
                  key: 'excel',
                  label: '导出为Excel',
                  icon: <DownloadOutlined />,
                  onClick: () => handleExport('excel')
                }
              ]
            }}
            trigger={['click']}
          >
            <Button icon={<DownloadOutlined />}>
              导出
            </Button>
          </Dropdown>,
          <Button key="clear" danger onClick={handleClearAll}>
            清空所有
          </Button>,
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            关闭
          </Button>
        ]}
      >
        <List
          dataSource={failedFiles}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个失败文件`,
          }}
          renderItem={(item) => {
            const errorType = getErrorType(item.errorMessage);
            return (
              <List.Item
                actions={[
                  <Button
                    key="remove"
                    type="link"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveFile(item.fileName)}
                  >
                    移除
                  </Button>
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Text strong>{item.fileName}</Text>
                      <Tag color={errorType.color}>{errorType.text}</Tag>
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size="small">
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        错误信息: {item.errorMessage}
                      </Text>
                      <Space size="small">
                        {item.fileSize && (
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            大小: {formatFileSize(item.fileSize)}
                          </Text>
                        )}
                        {item.fileType && (
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            类型: {item.fileType}
                          </Text>
                        )}
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          时间: {formatTimestamp(item.timestamp)}
                        </Text>
                      </Space>
                    </Space>
                  }
                />
              </List.Item>
            );
          }}
        />
      </Modal>
    </>
  );
};

export default FailedFiles;
